import React, { memo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const CONSENT_KEY = 'ftr_consent';

const DEFAULT_CONSENT = { analytics: false, marketing: false, functional: true };

const ConsentBanner = () => {
    const [visible, setVisible] = useState(false);
    const [consent, setConsent] = useState(DEFAULT_CONSENT);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(CONSENT_KEY);
            if (raw) {
                setConsent(JSON.parse(raw));
                return;
            }
        } catch (e) {
        }
        setVisible(true);
    }, []);

    const save = (next) => {
        const toSave = { ...consent, ...next };
        localStorage.setItem(CONSENT_KEY, JSON.stringify(toSave));
        setConsent(toSave);
        setVisible(false);
        try {
            window.dispatchEvent(new CustomEvent('ftr_consent_changed', { detail: toSave }));
        } catch (e) {
        }
    };

    const acceptAll = () => {
        save({ analytics: true, marketing: true, functional: true });
    };

    const rejectNonEssential = () => {
        save(DEFAULT_CONSENT);
    };

    if (!visible) return null;

    return (
        <div
            className="fixed bottom-0 left-0 right-0 sm:bottom-4 sm:left-6 sm:right-6 bg-white border rounded-t-[20px] rounded-b-none sm:rounded-[20px] p-4 sm:p-5 shadow-lg z-[9999] lg:bottom-4 lg:left-auto lg:right-6 lg:w-96"
            role="dialog" aria-label="Cookie consent">
            <div className="flex flex-col gap-3 sm:gap-4">
                <div>
                    <h3 className="text-sm sm:text-base font-bold text-gray-900">We value your privacy</h3>
                    <p className="mt-1 sm:mt-2 text-xs sm:text-sm leading-5 sm:leading-6 text-gray-600">
                        We use cookies to enhance browsing, analyze traffic, and personalize content. Choose which
                        categories to allow.
                    </p>
                </div>
                <fieldset className="space-y-1 sm:space-y-2">
                    <legend className="sr-only">Cookie preferences - choose which categories to allow</legend>
                    <div
                        className="flex items-start gap-2 sm:gap-3 cursor-pointer select-none py-0.5 sm:py-1 -mx-1 px-1 rounded-xl hover:bg-gray-50 transition"
                        onClick={() => {
                        }}>
                        <input type="checkbox" checked={consent.functional} disabled
                               aria-label="Functional cookies (required)"
                               className="mt-0.5 accent-emerald-600 pointer-events-auto shrink-0" />
                        <div className="pointer-events-none min-w-0">
                            <span
                                className="text-xs sm:text-sm font-semibold text-gray-800">Functional (required)</span>
                            <p className="text-xs text-gray-500 hidden sm:block">Essential for the website to function
                                properly. Cannot be disabled.</p>
                        </div>
                    </div>
                    <label
                        className="flex items-start gap-2 sm:gap-3 cursor-pointer select-none py-0.5 sm:py-1 -mx-1 px-1 rounded-xl hover:bg-gray-50 transition">
                        <input type="checkbox" checked={consent.analytics}
                               aria-label="Analytics cookies"
                               onChange={(e) => setConsent(prev => ({ ...prev, analytics: e.target.checked }))}
                               className="mt-0.5 accent-emerald-600 pointer-events-auto shrink-0" />
                        <div className="pointer-events-none min-w-0">
                            <span className="text-xs sm:text-sm font-semibold text-gray-800">Analytics</span>
                            <p className="text-xs text-gray-500 hidden sm:block">Help us understand how visitors use the
                                site so we can improve it.</p>
                        </div>
                    </label>
                    <label
                        className="flex items-start gap-2 sm:gap-3 cursor-pointer select-none py-0.5 sm:py-1 -mx-1 px-1 rounded-xl hover:bg-gray-50 transition">
                        <input type="checkbox" checked={consent.marketing}
                               aria-label="Marketing cookies"
                               onChange={(e) => setConsent(prev => ({ ...prev, marketing: e.target.checked }))}
                               className="mt-0.5 accent-emerald-600 pointer-events-auto shrink-0" />
                        <div className="pointer-events-none min-w-0">
                            <span className="text-xs sm:text-sm font-semibold text-gray-800">Marketing</span>
                            <p className="text-xs text-gray-500 hidden sm:block">Allow us to send you relevant
                                promotional content and offers.</p>
                        </div>
                    </label>
                </fieldset>
                <div className="flex flex-col gap-1.5 sm:gap-2">
                    <button type="button"
                            className="w-full rounded-xl sm:rounded-[20px] bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 cursor-pointer"
                            onClick={acceptAll}
                    >
                        Accept All
                    </button>
                    <button type="button"
                            className="w-full rounded-xl sm:rounded-[20px] border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 cursor-pointer"
                            onClick={rejectNonEssential}
                    >
                        Reject Non-essential
                    </button>
                    <button type="button"
                            className="w-full rounded-xl sm:rounded-[20px] px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-500 transition hover:bg-gray-100 cursor-pointer"
                            onClick={() => save({})}
                    >
                        Save Preferences
                    </button>
                </div>
                <p className="text-[11px] sm:text-xs text-gray-400">
                    By using our site, you agree to our{' '}
                    <Link to="/privacy-policy" className="underline hover:text-gray-600">Privacy Policy</Link>
                    {' '}and{' '}
                    <Link to="/cookie-policy" className="underline hover:text-gray-600">Cookie Policy</Link>.
                </p>
            </div>
        </div>
    );
};

export default memo(ConsentBanner);
